import {
  type Configuration,
  type DirectoryEntityType,
  type Snapshot,
  type SearchDirectoryEntitiesResponse,
  GetGwsBackupFilterApi,
  ListGoogleWorkspaceGroupsApi,
  ListGoogleWorkspaceOrgUnitsApi,
  ListInventoryItemSnapshotsApi,
  ListMicrosoft365UsersApi,
  ListMicrosoft365GroupsApi,
  ListMicrosoft365SharePointSitesApi,
  UpdateShouldFullBackupResourceApi,
  FileExplorerListPathApi,
  FileExplorerCountPathFilesApi,
  SearchDirectoryEntitiesApi,
} from '@repo/api-gw-sdk';
import { mutate } from 'swr';

import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';

const backupSettingsUrl = '/inventory/backup-settings';
const orgUnitsUrl = '/saas-inventory/google-workspace/org-units';
const groupsUrl = '/saas-inventory/google-workspace/groups';
const snapshotsBaseUrl = '/saas-inventory/google-workspace';
const m365UsersUrl = '/saas-inventory/microsoft-365/users';
const m365GroupsUrl = '/saas-inventory/microsoft-365/groups';
const m365SharePointSitesUrl = '/saas-inventory/microsoft-365/sharepoint-sites';

const TRASH_PREFIX = 'trash/';
// One over the UI display threshold so the badge can render "10000+" without an exact count.
const TRASH_COUNT_THRESHOLD = 10001;

function snapshotToGWSnapshot(
  snapshot: Snapshot,
  isLatest: boolean
): GWSnapshot {
  const date = (snapshot.pointInTime ?? snapshot.createdTime).toISOString();
  return {
    id: snapshot.id,
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: snapshot.fullSizeBytes ?? 0,
    userCount: 0,
    sharedDriveCount: 0,
    userDailyBudgetExceededItemsCount:
      snapshot.userDailyBudgetExceededItemsCount,
  };
}

export const saasInventory = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const getBackupSettingsApi = new GetGwsBackupFilterApi(config);
  const updateBackupSettingsApi = new UpdateShouldFullBackupResourceApi(config);
  const listOrgUnitsApi = new ListGoogleWorkspaceOrgUnitsApi(config);
  const listGroupsApi = new ListGoogleWorkspaceGroupsApi(config);
  const listSnapshotsApi = new ListInventoryItemSnapshotsApi(config);
  const listM365UsersApi = new ListMicrosoft365UsersApi(config);
  const listM365GroupsApi = new ListMicrosoft365GroupsApi(config);
  const listM365SharePointSitesApi = new ListMicrosoft365SharePointSitesApi(
    config
  );
  const listPathApi = new FileExplorerListPathApi(config);
  const countPathFilesApi = new FileExplorerCountPathFilesApi(config);
  const searchDirectoryApi = new SearchDirectoryEntitiesApi(config);

  return {
    googleWorkspace: {
      useSnapshots: (resourceId: string | null) => {
        const result = httpClient.execute(
          listSnapshotsApi.listInventoryItemSnapshots.bind(listSnapshotsApi),
          resourceId ? snapshotsBaseUrl : null,
          'snapshots',
          currentProjectId,
          resourceId ?? '',
          0,
          100,
          'eon'
        );
        const snapshots: GWSnapshot[] = (result.data?.data ?? []).map(
          (s: Snapshot, i: number) => snapshotToGWSnapshot(s, i === 0)
        );
        return {
          data: snapshots,
          isLoading: result.isLoading,
        };
      },
      useListPath: (
        resourceId: string,
        prefix: string,
        pageSize: number,
        snapshotId?: string,
        pageToken?: string,
        queryExecutionId?: string,
        sortField?: string,
        sortDir?: string
      ) =>
        httpClient.execute(
          listPathApi.fileExplorerListPath.bind(listPathApi),
          resourceId ? snapshotsBaseUrl : null,
          'listPath',
          currentProjectId,
          resourceId,
          prefix,
          pageSize,
          pageToken,
          queryExecutionId,
          undefined,
          snapshotId,
          sortField,
          sortDir
        ),
      useTrashCount: (resourceId: string, snapshotId?: string) =>
        httpClient.execute(
          countPathFilesApi.fileExplorerCountPathFiles.bind(countPathFilesApi),
          resourceId && snapshotId ? snapshotsBaseUrl : null,
          'trashCount',
          currentProjectId,
          resourceId,
          TRASH_PREFIX,
          snapshotId,
          TRASH_COUNT_THRESHOLD
        ),
      // Unlike useGroups (live /groups/list discovery), this reads the per-snapshot
      // directory_entities index — it reacts to the selected snapshot and is empty
      // for pre-index snapshots.
      useDirectorySearch: ({
        resourceId,
        snapshotId,
        query,
        entityType,
        pageIndex,
        pageSize,
      }: {
        resourceId: string;
        snapshotId?: string;
        query?: string;
        entityType?: DirectoryEntityType;
        pageIndex: number;
        pageSize: number;
      }) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof searchDirectoryApi.searchDirectoryEntities
            >
          ) =>
            searchDirectoryApi.searchDirectoryEntities(...args).then(
              (response) => response,
              (error) => {
                if (error.code === 404) {
                  // No directory index for this snapshot (e.g. older snapshot) —
                  // surface an empty list rather than an error.
                  return {
                    data: [],
                    pagination: { recordsCount: 0 },
                  } satisfies SearchDirectoryEntitiesResponse;
                }
                throw error;
              }
            ),
          resourceId && snapshotId ? snapshotsBaseUrl : null,
          'directorySearch',
          currentProjectId,
          resourceId,
          { snapshotId: snapshotId ?? '', query, entityType },
          pageIndex,
          pageSize
        ),
      useOrgUnits: (connectorId: string) =>
        httpClient.execute(
          listOrgUnitsApi.listGoogleWorkspaceOrgUnits.bind(listOrgUnitsApi),
          orgUnitsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useGroups: (connectorId: string) =>
        httpClient.execute(
          listGroupsApi.listGoogleWorkspaceGroups.bind(listGroupsApi),
          groupsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useBackupSettings: (resourceId: string) =>
        httpClient.execute(
          getBackupSettingsApi.getGwsBackupFilter.bind(getBackupSettingsApi),
          backupSettingsUrl,
          resourceId,
          currentProjectId,
          resourceId
        ),
      updateBackupSettings: (resourceId: string, gwsFilterJson: string) =>
        updateBackupSettingsApi
          .updateShouldFullBackupResource(currentProjectId, resourceId, {
            gwsFilterJson,
          })
          .then(() =>
            mutate([
              backupSettingsUrl,
              resourceId,
              currentProjectId,
              resourceId,
            ])
          ),
    },
    microsoft365: {
      useUsers: (connectorId: string) =>
        httpClient.execute(
          listM365UsersApi.listMicrosoft365Users.bind(listM365UsersApi),
          m365UsersUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useGroups: (connectorId: string) =>
        httpClient.execute(
          listM365GroupsApi.listMicrosoft365Groups.bind(listM365GroupsApi),
          m365GroupsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useSharePointSites: (connectorId: string) =>
        httpClient.execute(
          listM365SharePointSitesApi.listMicrosoft365SharePointSites.bind(
            listM365SharePointSitesApi
          ),
          m365SharePointSitesUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
    },
  };
};
